// Best-effort OCR extraction for the "Populate values" admin feature.
// Runs entirely client-side via tesseract.js (no backend, no API key/cost) —
// same trade-off as the link-based scraper: it's best-effort and should
// always be reviewed before saving.
//
// Two extraction passes are combined for robustness:
//   1. Word-position table reconstruction — groups OCR'd words into visual
//      rows by Y-position, then splits each row into label/value at its
//      widest horizontal gap. This correctly handles real two-column spec
//      tables even when Tesseract's own text-linearization scrambles them.
//   2. Plain-text line parsing (colon or big-space split) — a fallback that
//      still works even if word-level bounding-box data isn't available.

function median(arr) {
  if (!arr.length) return 0;
  const sorted = [...arr].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

function cleanText(s) {
  return String(s || '').replace(/[ \t]+/g, ' ').trim();
}

function isJunkLabel(s) {
  return !s || s.length > 40 || /^[0-9.,₹$%-]+$/.test(s);
}
function isJunkValue(s) {
  return !s || s.length > 150;
}

// --- Pass 1: word-position table reconstruction ------------------------

function collectWords(data) {
  if (Array.isArray(data.words) && data.words.length) return data.words;
  if (Array.isArray(data.blocks)) {
    const out = [];
    for (const b of data.blocks) {
      for (const p of b.paragraphs || []) {
        for (const l of p.lines || []) {
          for (const w of l.words || []) out.push(w);
        }
      }
    }
    if (out.length) return out;
  }
  return [];
}

function groupWordsIntoRows(words) {
  const usable = words
    .filter(w => w.text && w.text.trim() && w.bbox && (w.confidence === undefined || w.confidence >= 30))
    .map(w => ({
      text: w.text.trim(),
      x0: w.bbox.x0, x1: w.bbox.x1,
      yCenter: (w.bbox.y0 + w.bbox.y1) / 2,
      height: w.bbox.y1 - w.bbox.y0,
    }))
    .sort((a, b) => a.yCenter - b.yCenter);

  if (usable.length === 0) return [];

  const rowThreshold = Math.max(8, median(usable.map(w => w.height)) * 0.6);
  const rows = [];
  for (const w of usable) {
    let row = rows.find(r => Math.abs(r.yCenter - w.yCenter) < rowThreshold);
    if (!row) {
      row = { yCenter: w.yCenter, words: [] };
      rows.push(row);
    }
    row.words.push(w);
    row.yCenter = row.words.reduce((s, x) => s + x.yCenter, 0) / row.words.length;
  }
  rows.forEach(r => r.words.sort((a, b) => a.x0 - b.x0));
  return rows.sort((a, b) => a.yCenter - b.yCenter);
}

function rowToLabelValue(row) {
  const words = row.words;
  if (words.length < 2) return null;

  const gaps = [];
  for (let i = 1; i < words.length; i++) gaps.push(words[i].x0 - words[i - 1].x1);
  const avgGap = gaps.reduce((s, g) => s + g, 0) / gaps.length;

  let splitIdx = -1, maxGap = -Infinity;
  gaps.forEach((g, i) => { if (g > maxGap) { maxGap = g; splitIdx = i; } });
  if (splitIdx === -1 || maxGap < Math.max(15, avgGap * 2.5)) return null;

  const label = cleanText(words.slice(0, splitIdx + 1).map(w => w.text).join(' '));
  const value = cleanText(words.slice(splitIdx + 1).map(w => w.text).join(' '));
  if (isJunkLabel(label) || isJunkValue(value)) return null;
  return { label, value };
}

function specsFromWords(data) {
  const words = collectWords(data);
  if (words.length === 0) return [];
  const rows = groupWordsIntoRows(words);
  return rows.map(rowToLabelValue).filter(Boolean);
}

// --- Pass 2: plain-text line fallback -----------------------------------

function splitKeyValue(line) {
  const colonIdx = line.indexOf(':');
  if (colonIdx > 0 && colonIdx < line.length - 1) {
    const label = cleanText(line.slice(0, colonIdx));
    const value = cleanText(line.slice(colonIdx + 1));
    if (!isJunkLabel(label) && !isJunkValue(value)) return { label, value };
  }
  const gapMatch = line.match(/^(.{2,40}?)\s{2,}(.{2,150})$/);
  if (gapMatch) {
    const label = cleanText(gapMatch[1]);
    const value = cleanText(gapMatch[2]);
    if (!isJunkLabel(label) && !isJunkValue(value)) return { label, value };
  }
  return null;
}

function specsFromText(text) {
  return String(text || '')
    .split('\n')
    .map(cleanText)
    .filter(Boolean)
    .map(splitKeyValue)
    .filter(Boolean);
}

// --- Name heuristic -------------------------------------------------------

function guessName(data) {
  const words = collectWords(data);
  const lines = String(data.text || '').split('\n').map(cleanText).filter(Boolean);
  const looksLikeSpecRow = (l) => splitKeyValue(l) !== null;
  const isBoilerplate = (l) => /^[A-Z\s0-9!.,'-]+$/.test(l) && l.length < 30;

  if (words.length) {
    // Prefer the tallest line of text on the page — titles are usually the
    // largest font on a product page screenshot.
    const rows = groupWordsIntoRows(words);
    const candidates = rows
      .map(r => ({
        text: cleanText(r.words.map(w => w.text).join(' ')),
        height: median(r.words.map(w => w.height)),
      }))
      .filter(c => c.text.length >= 10 && c.text.length <= 150)
      .filter(c => !looksLikeSpecRow(c.text) && !isBoilerplate(c.text));
    if (candidates.length) {
      candidates.sort((a, b) => b.height - a.height);
      return candidates[0].text;
    }
  }

  const fallback = lines.find(l =>
    l.length >= 10 && l.length <= 150 && !looksLikeSpecRow(l) && !isBoilerplate(l)
  );
  return fallback || lines[0] || '';
}

// --- Public API -------------------------------------------------------

function mergeSpecs(specLists) {
  const merged = [];
  const seen = new Set();
  for (const list of specLists) {
    for (const { label, value } of list) {
      const k = label.toLowerCase();
      if (seen.has(k)) continue;
      seen.add(k);
      merged.push({ label, value });
    }
  }
  return merged.slice(0, 20);
}

// --- Image preprocessing -------------------------------------------------
// Tesseract is dramatically more accurate on large, high-contrast, low-noise
// input than on a raw web-page screenshot full of small light-grey text. We
// upscale the image to a comfortable working width and push it to a punchy
// grayscale before recognition — this is the single biggest accuracy lever.

function loadImageFromFile(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
    img.src = url;
  });
}

async function preprocessImage(file) {
  try {
    const img = await loadImageFromFile(file);
    // Upscale narrow screenshots up to ~3x so small text becomes legible to OCR.
    const targetW = 1800;
    const scale = Math.min(3, Math.max(1, targetW / img.width));
    const w = Math.round(img.width * scale);
    const h = Math.round(img.height * scale);

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, 0, 0, w, h);

    const id = ctx.getImageData(0, 0, w, h);
    const d = id.data;
    const contrast = 1.45;                 // stretch dark text away from light bg
    const intercept = 128 * (1 - contrast);
    for (let i = 0; i < d.length; i += 4) {
      let g = 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
      g = g * contrast + intercept;
      d[i] = d[i + 1] = d[i + 2] = g < 0 ? 0 : g > 255 ? 255 : g;
    }
    ctx.putImageData(id, 0, 0);
    return canvas;
  } catch {
    return file; // fall back to the raw file if anything goes wrong
  }
}

// Runs OCR on one or more screenshots (sharing a single worker for speed)
// and merges everything into one result — covers spreading a product's
// title, spec table, and extra details across multiple screenshots.
export async function extractFromScreenshots(files, onProgress) {
  const fileList = Array.isArray(files) ? files : [files];
  if (fileList.length === 0) throw new Error('No screenshots selected.');

  const reportProgress = onProgress || (() => {});
  const progress = { index: 0 };

  const { createWorker } = await import('tesseract.js');
  // Use the high-accuracy "best" LSTM float models (tessdata_best) rather than
  // the default fast/int models — noticeably better recognition on small,
  // anti-aliased screenshot text. Still 100% open-source and local (the data
  // is downloaded once from the public tessdata CDN, then cached; no API).
  const worker = await createWorker('eng', 1, {
    langPath: 'https://tessdata.projectnaptha.com/4.0.0_best',
    logger: (m) => {
      if (m.status === 'recognizing text' && typeof m.progress === 'number') {
        const overall = (progress.index + m.progress) / fileList.length;
        reportProgress(Math.round(overall * 100));
      }
    },
  });
  // Keep inter-word spacing (the column split relies on big gaps), and treat
  // each screenshot as a single uniform block so label/value stay on one line.
  await worker.setParameters({
    preserve_interword_spaces: '1',
    tessedit_pageseg_mode: '6',
  });

  try {
    const perImageSpecs = [];
    const nameCandidates = [];
    const rawTexts = [];

    for (let i = 0; i < fileList.length; i++) {
      progress.index = i;
      const input = await preprocessImage(fileList[i]);
      const { data } = await worker.recognize(input);

      perImageSpecs.push(specsFromWords(data), specsFromText(data.text));
      rawTexts.push(data.text || '');

      const name = guessName(data);
      if (name) nameCandidates.push(name);
    }

    reportProgress(100);

    const specs = mergeSpecs(perImageSpecs);
    // Prefer the longest name candidate — usually the most complete title.
    const name = nameCandidates.sort((a, b) => b.length - a.length)[0] || '';
    const text = rawTexts.join('\n');

    if (!name && specs.length === 0 && !text.trim()) {
      throw new Error('No readable product details found in these screenshots. Try clearer, more zoomed-in images.');
    }

    // `text` is the full raw OCR dump — the offline field extractor mines it
    // directly, so even screenshots with no clean spec table still yield data.
    return { name, specs, text };
  } finally {
    await worker.terminate();
  }
}
